<script setup lang="ts">
import type { RoomInvite } from '@ddt/protocol';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useNav } from '../navigation';
import { useMatch } from '../stores/match';

/**
 * Convite de sala de um amigo. Aparece em qualquer tela (menos durante a partida) e some sozinho depois de 30 s.
 * Aceitar leva à sala de PvP, que troca a sala do jogador pela do amigo (o código vai no convite); recusar só dispensa.
 */
const match = useMatch();
const nav = useNav();
const TTL_MS = 30_000;

const visible = computed(() => (match.phase === 'playing' ? [] : match.invites));
/** Quando cada convite chegou: o que passou de 30 s sai. */
const seenAt = new Map<string, number>();
const tick = ref(0);
const timer = setInterval(() => (tick.value += 1), 1000);
onBeforeUnmount(() => clearInterval(timer));

watch(
  () => match.invites,
  (invites) => {
    for (const i of invites) if (!seenAt.has(key(i))) seenAt.set(key(i), Date.now());
  },
  { immediate: true, deep: true },
);
watch(tick, () => {
  for (const i of match.invites) if (Date.now() - (seenAt.get(key(i)) ?? Date.now()) > TTL_MS) match.dismissInvite(i.fromAccountId);
});

const key = (i: RoomInvite) => `${i.fromAccountId}:${i.joinCode}`;

function accept(invite: RoomInvite) {
  match.acceptInvite(invite);
  if (nav.current.value.name !== 'pvp') nav.push('pvp');
}
</script>

<template>
  <div v-if="visible.length > 0" class="rit" role="region" aria-label="Convites de sala" data-testid="invites">
    <div v-for="invite in visible" :key="key(invite)" class="rit__card" role="alert" :data-testid="`invite-${invite.fromAccountId}`">
      <p class="rit__text"><strong>{{ invite.fromNick }}</strong> convidou você para a sala de PvP.</p>
      <div class="rit__actions">
        <button type="button" class="rit__btn" :data-testid="`invite-accept-${invite.fromAccountId}`" @click="accept(invite)">Aceitar</button>
        <button type="button" class="rit__btn rit__btn--ghost" :data-testid="`invite-decline-${invite.fromAccountId}`" @click="match.dismissInvite(invite.fromAccountId)">Recusar</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.rit {
  position: fixed;
  top: 64px;
  right: 16px;
  z-index: 80;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(320px, calc(100vw - 32px));
}
.rit__card {
  padding: 12px 14px;
  color: #fff;
  border: 3px solid #e0a93e;
  border-radius: 14px;
  background: linear-gradient(180deg, #221d31, #120f1a);
  box-shadow: 0 10px 28px rgb(0 0 0 / 60%);
  animation: rit-in 0.3s ease-out both;
}
.rit__text {
  margin: 0 0 10px;
  font-weight: 600;
}
.rit__text strong {
  color: #ffe9a0;
}
.rit__actions {
  display: flex;
  gap: 8px;
}
.rit__btn {
  padding: 4px 16px;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  color: #2a1a08;
  cursor: pointer;
  border: 2px solid #ffe9a0;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffe9a0, #e0a93e);
}
.rit__btn--ghost {
  color: #fff;
  border-color: #4a3512;
  background: #16131f;
}
.rit__btn:hover {
  filter: brightness(1.1);
}
.rit__btn:focus-visible {
  outline: 3px solid #7fe3ff;
  outline-offset: 2px;
}
@keyframes rit-in {
  from {
    opacity: 0;
    transform: translateX(24px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .rit__card {
    animation: none;
  }
}
</style>
